import type { SortDirection } from "@opensea/graphql"
import type { PaginatorOptions, Value } from "./pagination-utils"
import {
  DEFAULT_PAGINATOR_OPTIONS,
  insertInOrder,
  remove,
  updateInOrder,
} from "./pagination-utils"

export type Sort<F extends string> = {
  by?: F | undefined
  direction?: SortDirection | undefined
}

/**
 * This class implements logic for inserting elements into paginated lists. It does this as efficiently
 * as possible, while also handling various edge cases, such as:
 * - duplicates
 * - list re-ordering when updating a non unique sorted field (e.g volume)
 * - ...
 *
 * It works on one simple assumption: last sort value is sortable & unique (e.g ulid)
 */
export abstract class Paginator<T, F extends string> {
  private readonly options: PaginatorOptions<T>

  constructor(opts: PaginatorOptions<T> = DEFAULT_PAGINATOR_OPTIONS) {
    this.options = opts
  }

  protected abstract getValues(item: T, sortBy: F | undefined): Value[]

  protected inverseDirection(
    direction: SortDirection | undefined,
  ): SortDirection | undefined {
    switch (direction) {
      case "ASC":
        return "DESC"
      case "DESC":
        return "ASC"
      default:
        return direction
    }
  }

  protected getSortDirection(
    sort: Sort<F> | undefined,
  ): SortDirection | undefined {
    return sort?.direction
  }

  public remove(item: T, items: T[], sort: Sort<F>): T[] {
    return remove(
      item,
      items,
      element => this.getValues(element, sort.by),
      this.getSortDirection(sort),
      this.options.applyIf,
    )
  }

  public insertInOrder(
    item: T,
    items: T[],
    sort: Sort<F> | undefined,
    options?: Partial<PaginatorOptions<T>>,
  ): T[] {
    return insertInOrder(
      item,
      items,
      element => this.getValues(element, sort?.by),
      this.getSortDirection(sort),
      { ...this.options, ...options },
    )
  }

  public updateInOrder(
    items: T[],
    sort: Sort<F> | undefined,
    matchItem: (t: T) => boolean,
    updateFunction: (t: T) => T,
  ): T[] {
    return updateInOrder(
      items,
      element => this.getValues(element, sort?.by),
      this.getSortDirection(sort),
      matchItem,
      updateFunction,
      this.options,
    )
  }
}
